import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import InfoPopover from '../ui/InfoPopover';
import EmptyFieldPolicyControl from './EmptyFieldPolicyControl';
import type { ContentMasterBulkEstimateView } from '../../api/generated/models/ContentMasterBulkEstimateView';
import type {
  ContentMasterEmptyFieldPolicy,
  ContentMasterSuperBatchListingConfig,
} from '../../store/slices/contentMasterSlice';
import { MARKETPLACE_CODES, type MarketplaceCode, marketplaceLabel } from '../../utils/marketplaceListing';
import AutoDetectCategoryWarningModal, {
  isAutoDetectWarningDismissed,
} from './AutoDetectCategoryWarningModal';

/**
 * Listing-flow state of the super-batch modal. The listing flow itself is
 * platform-driven (SCX marketplace channel → listing, otherwise classic CM
 * generation) - there is no user toggle. The parameter fields mirror the bulk
 * listing modal 1:1 (same defaults: required + recommended on).
 */
export interface SuperBatchListingState {
  /** Marketplace override; `null` = auto-detect from the channel. */
  marketplace: MarketplaceCode | null;
  generateRequired: boolean;
  generateRecommended: boolean;
  generateOther: boolean;
  autoDetectCategory: boolean;
  generatePrefilled: boolean;
  /** "Fill only empty fields" (`listingConfig.onlyEmptyFields`). */
  onlyEmptyFields: boolean;
}

export const defaultSuperBatchListingState = (): SuperBatchListingState => ({
  marketplace: null,
  generateRequired: true,
  generateRecommended: true,
  generateOther: false,
  autoDetectCategory: false,
  generatePrefilled: false,
  onlyEmptyFields: false,
});

/**
 * The `listingConfig` object the super-batch routes expect for this state.
 * The caller sends it only on marketplace channels (classic flow elsewhere)
 * and must send the SAME object to the estimate and the submit (spec).
 */
export const toSuperBatchListingConfig = (
  state: SuperBatchListingState
): ContentMasterSuperBatchListingConfig => ({
  marketplace: state.marketplace,
  generateRequired: state.generateRequired,
  generateRecommended: state.generateRecommended,
  generateOther: state.generateOther,
  autoDetectCategory: state.autoDetectCategory,
  generatePrefilled: state.generatePrefilled,
  onlyEmptyFields: state.onlyEmptyFields,
});

export interface SuperBatchConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** `POST /bulk/estimate-by-filters` response (`null` while loading / failed). */
  estimate: ContentMasterBulkEstimateView | null;
  estimateLoading: boolean;
  /** `POST /bulk/generate-by-filters` in flight. */
  isSubmitting: boolean;
  /**
   * SCX marketplace channel → the listing flow is active and its parameter
   * controls are shown (platform-driven, no user toggle). On other channels
   * the classic CM generation runs.
   */
  listingAvailable: boolean;
  /** Controlled listing-flow state; owned by the page. */
  listing: SuperBatchListingState;
  onListingChange: (next: SuperBatchListingState) => void;
  /**
   * Controlled "also generate URL paths" opt-in (`includeUrlPath` of the
   * classic-flow super-batch routes; default false). Owned by the page so the
   * SAME value is sent to estimate-by-filters AND generate-by-filters -
   * otherwise the token estimate diverges (one extra field per product).
   * Changing it must invalidate the current estimate (like `onListingChange`).
   * Hidden on marketplace channels (the flag does not apply to the listing flow).
   */
  includeUrlPath: boolean;
  onIncludeUrlPathChange: (next: boolean) => void;
  /**
   * Controlled "what to do with empty fields?" choice (`emptyFieldPolicy` of
   * the classic-flow `POST /bulk/generate-by-filters`; default `keepOriginal`).
   * Owned by the page. Unlike `includeUrlPath` it does NOT invalidate the
   * estimate: `POST /bulk/estimate-by-filters` ignores the parameter (no cost
   * impact). Hidden on marketplace channels (no effect on the listing flow).
   */
  emptyFieldPolicy: ContentMasterEmptyFieldPolicy;
  onEmptyFieldPolicyChange: (next: ContentMasterEmptyFieldPolicy) => void;
  /** Runs `POST /bulk/estimate-by-filters` with the modal's current parameters. */
  onEstimate: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation modal of the super batch ("generate ALL products matching the
 * current search filters"). The estimate is NOT requested on open: the user
 * configures the parameters, presses "Estimate", reviews the numbers (how
 * many products are affected, the minimum token cost - always a very rough
 * estimate, and that the job stops when tokens run out) and confirms via a
 * consent checkbox. "Start generation" stays disabled until an estimate for
 * the current parameters exists AND the consent box is checked; any parameter
 * change invalidates both (the page clears the estimate). A
 * `wouldExceedBudget` estimate strengthens the warning and shows the resume
 * hint (re-run with the "New" status filter) but does NOT block the submit;
 * `productsCount === 0` disables it. There is no time estimate and no job
 * status/cancel - by design (see the backend contract).
 *
 * On SCX marketplace channels the modal shows the listing-flow controls (same
 * as the bulk listing modal) - the flow itself is derived from the platform.
 */
const SuperBatchConfirmModal: React.FC<SuperBatchConfirmModalProps> = ({
  isOpen,
  onClose,
  estimate,
  estimateLoading,
  isSubmitting,
  listingAvailable,
  listing,
  onListingChange,
  includeUrlPath,
  onIncludeUrlPathChange,
  emptyFieldPolicy,
  onEmptyFieldPolicyChange,
  onEstimate,
  onConfirm,
}) => {
  const { t } = useTranslation();

  /** Consent step before enabling auto-detect (categories are written to Wawi). */
  const [autoDetectWarningOpen, setAutoDetectWarningOpen] = useState(false);
  /** "I have reviewed the estimate" checkbox - unlocks "Start generation". */
  const [estimateAccepted, setEstimateAccepted] = useState(false);

  // Re-reviewing is mandatory: reset the consent whenever the estimate is
  // (re)requested, invalidated by a parameter change, or the modal reopens.
  useEffect(() => {
    setEstimateAccepted(false);
  }, [estimate, isOpen]);

  const productsCount = estimate?.productsCount ?? 0;
  const noProducts = estimate != null && productsCount === 0;
  const listingActive = listingAvailable;
  const anyGroupSelected =
    listing.generateRequired || listing.generateRecommended || listing.generateOther;
  const paramsValid = !listingActive || anyGroupSelected;
  const canEstimate = !estimateLoading && !isSubmitting && paramsValid;
  const canSubmit =
    !estimateLoading &&
    !isSubmitting &&
    estimate != null &&
    !noProducts &&
    paramsValid &&
    estimateAccepted;

  // The controls are locked while an estimate is in flight (no divergence
  // between the shown numbers and the parameters they were computed with).
  const controlsDisabled = isSubmitting || estimateLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSubmitting ? () => undefined : onClose}
      title=""
      hideCloseButton
      isCentered
      className="content-master-bulk-modal content-master-super-batch-modal"
    >
      <div className="content-master-bulk-modal__inner">
        <h3 className="content-master-bulk-modal__title">
          {t('cm_super_batch_modal_title')}
        </h3>

        {listingAvailable ? (
          <div className="content-master-bulk-modal__marketplace">
                <fieldset className="content-master-bulk-modal__groups">
                  <legend className="content-master-bulk-modal__field-label">
                    {t('marketplace_listing_groups_legend')}
                  </legend>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={listing.generateRequired}
                      disabled={controlsDisabled}
                      onChange={(event) =>
                        onListingChange({ ...listing, generateRequired: event.target.checked })
                      }
                    />
                    <span>{t('marketplace_listing_group_required')}</span>
                  </label>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={listing.generateRecommended}
                      disabled={controlsDisabled}
                      onChange={(event) =>
                        onListingChange({
                          ...listing,
                          generateRecommended: event.target.checked,
                        })
                      }
                    />
                    <span>{t('marketplace_listing_group_recommended')}</span>
                  </label>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={listing.generateOther}
                      disabled={controlsDisabled}
                      onChange={(event) =>
                        onListingChange({ ...listing, generateOther: event.target.checked })
                      }
                    />
                    <span>{t('marketplace_listing_group_other')}</span>
                  </label>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={listing.generatePrefilled}
                      disabled={controlsDisabled}
                      onChange={(event) =>
                        onListingChange({
                          ...listing,
                          generatePrefilled: event.target.checked,
                        })
                      }
                    />
                    <span>{t('marketplace_listing_generate_prefilled_label')}</span>
                    <InfoPopover
                      text={t('marketplace_listing_generate_prefilled_tooltip')}
                      ariaLabel={t('marketplace_listing_generate_prefilled_label')}
                    />
                  </label>
                  <label className="content-master-bulk-modal__checkbox">
                    <input
                      type="checkbox"
                      className="content-master-checkbox"
                      checked={listing.onlyEmptyFields}
                      disabled={controlsDisabled}
                      onChange={(event) =>
                        onListingChange({
                          ...listing,
                          onlyEmptyFields: event.target.checked,
                        })
                      }
                    />
                    <span>{t('marketplace_listing_only_empty_fields_label')}</span>
                    <InfoPopover
                      text={t('marketplace_listing_only_empty_fields_tooltip')}
                      ariaLabel={t('marketplace_listing_only_empty_fields_label')}
                    />
                  </label>
                </fieldset>

                <label className="content-master-bulk-modal__checkbox">
                  <input
                    type="checkbox"
                    className="content-master-checkbox"
                    checked={listing.autoDetectCategory}
                    disabled={controlsDisabled}
                    onChange={(event) => {
                      if (!event.target.checked) {
                        onListingChange({ ...listing, autoDetectCategory: false });
                      } else if (isAutoDetectWarningDismissed()) {
                        onListingChange({ ...listing, autoDetectCategory: true });
                      } else {
                        // Consent first: the flag is set on confirm.
                        setAutoDetectWarningOpen(true);
                      }
                    }}
                  />
                  <span>{t('marketplace_listing_auto_detect_category')}</span>
                </label>

                <label className="content-master-bulk-modal__field">
                  <span className="content-master-bulk-modal__field-label">
                    {t('marketplace_listing_select_marketplace')}
                  </span>
                  <select
                    className="content-master-bulk-modal__select"
                    value={listing.marketplace ?? ''}
                    disabled={controlsDisabled}
                    onChange={(event) =>
                      onListingChange({
                        ...listing,
                        marketplace: (event.target.value || null) as MarketplaceCode | null,
                      })
                    }
                  >
                    <option value="">{t('marketplace_listing_marketplace_auto')}</option>
                    {MARKETPLACE_CODES.map((code) => (
                      <option key={code} value={code}>
                        {marketplaceLabel(code)}
                      </option>
                    ))}
                  </select>
                </label>
          </div>
        ) : (
          <>
            <label className="content-master-bulk-modal__checkbox">
              <input
                type="checkbox"
                className="content-master-checkbox"
                checked={includeUrlPath}
                disabled={controlsDisabled}
                onChange={(event) => onIncludeUrlPathChange(event.target.checked)}
              />
              <span>{t('content_master_bulk_include_url_path')}</span>
              <InfoPopover
                text={t('content_master_bulk_include_url_path_hint')}
                ariaLabel={t('content_master_bulk_include_url_path')}
              />
            </label>
            {/* No estimate lock (`controlsDisabled`): the policy has no cost
                impact, so it may be changed after the estimate was requested. */}
            <EmptyFieldPolicyControl
              name="super-batch-empty-field-policy"
              value={emptyFieldPolicy}
              disabled={isSubmitting}
              onChange={onEmptyFieldPolicyChange}
            />
          </>
        )}

        {estimateLoading ? (
          <div className="content-master-super-batch-modal__loading">
            {t('cm_super_batch_estimate_loading')}
          </div>
        ) : estimate != null ? (
          <>
            <div className="content-master-super-batch-modal__warning" role="alert">
              <div>
                {t('cm_super_batch_warn_products_prefix')}{' '}
                <strong>{productsCount.toLocaleString()}</strong>{' '}
                {t('cm_super_batch_warn_products_suffix')}
              </div>
              <div>
                {t('cm_super_batch_warn_tokens_prefix')}{' '}
                <strong>{(estimate.estimatedTokens ?? 0).toLocaleString()}</strong>{' '}
                {t('cm_super_batch_warn_tokens_suffix')}
              </div>
              <div className="content-master-super-batch-modal__warning-stop">
                {t('cm_super_batch_warn_stop')}
              </div>
              {estimate.wouldExceedBudget === true ? (
                <div className="content-master-super-batch-modal__warning-exceed">
                  {t('cm_super_batch_exceed_warning')}
                </div>
              ) : null}
            </div>

            <div className="content-master-super-batch-modal__remaining">
              {t('cm_super_batch_tokens_remaining_label')}{' '}
              <strong>{(estimate.tokensRemaining ?? 0).toLocaleString()}</strong>
            </div>

            {estimate.wouldExceedBudget === true ? (
              <div className="content-master-super-batch-modal__resume-hint">
                {t('cm_super_batch_resume_hint')}
              </div>
            ) : null}

            {noProducts ? (
              <div className="content-master-super-batch-modal__no-products">
                {t('cm_super_batch_no_products')}
              </div>
            ) : null}
          </>
        ) : null}

        {estimate != null && !estimateLoading ? (
          <label className="content-master-bulk-modal__checkbox content-master-super-batch-modal__consent">
            <input
              type="checkbox"
              className="content-master-checkbox"
              checked={estimateAccepted}
              disabled={isSubmitting}
              onChange={(event) => setEstimateAccepted(event.target.checked)}
            />
            <span>{t('cm_super_batch_estimate_consent')}</span>
          </label>
        ) : null}

        {/* Left-to-right user sequence: Cancel → Estimate → Generate. The
            estimate is on demand and must run before the submit unlocks. */}
        <div className="content-master-bulk-modal__buttons">
          <button
            type="button"
            className="content-master-bulk-modal__button content-master-bulk-modal__button--cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('content_master_bulk_modal_cancel')}
          </button>
          <button
            type="button"
            className="content-master-bulk-modal__button content-master-bulk-modal__button--confirm"
            onClick={onEstimate}
            disabled={!canEstimate}
          >
            {estimateLoading
              ? t('cm_super_batch_estimate_loading')
              : t('cm_super_batch_estimate_button')}
          </button>
          <button
            type="button"
            className="content-master-bulk-modal__button content-master-bulk-modal__button--confirm"
            onClick={onConfirm}
            disabled={!canSubmit}
            title={
              noProducts
                ? t('cm_super_batch_no_products')
                : estimate == null
                ? t('cm_super_batch_estimate_required_hint')
                : !estimateAccepted
                ? t('cm_super_batch_estimate_consent_hint')
                : undefined
            }
          >
            {isSubmitting ? t('loading_text') : t('cm_super_batch_confirm')}
          </button>
        </div>
      </div>

      <AutoDetectCategoryWarningModal
        isOpen={autoDetectWarningOpen}
        onClose={() => setAutoDetectWarningOpen(false)}
        onConfirm={() => {
          setAutoDetectWarningOpen(false);
          onListingChange({ ...listing, autoDetectCategory: true });
        }}
      />
    </Modal>
  );
};

export default SuperBatchConfirmModal;
